import { useLogger } from '@/components/utils/LoggerProvider'
import { useWindowEvent } from '@/hooks/browser/useWindowEvent'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

export function readLocalStorage<T>(key: string, schema: z.ZodType<T>): T | undefined {
  const item = window.localStorage.getItem(key)
  if (item === null) {
    return undefined
  }
  try {
    const result = schema.safeParse(JSON.parse(item))
    return result.success ? result.data : undefined
  } catch {
    return undefined
  }
}

function updateLocalStorage<T>(key: string, value: T | undefined) {
  if (value === undefined) {
    window.localStorage.removeItem(key)
  } else {
    window.localStorage.setItem(key, JSON.stringify(value))
  }
}

export type LocalStorageHook<T> = (
  initialState?: T | (() => T)
) => [T | undefined, Dispatch<SetStateAction<T | undefined>>]

export function createLocalStorageHook<T extends {} | [] | string | number | boolean | undefined>(
  key: string,
  schema: z.ZodType<T>
): LocalStorageHook<T> {
  return (initialState?: T | (() => T)) => {
    const { logger } = useLogger()
    const [state, setState] = useState<T | undefined>(initialState)
    useEffect(() => {
      const value = readLocalStorage(key, schema)
      if (value !== undefined) {
        setState(value)
      }
    }, [])
    useWindowEvent('storage', (event) => {
      if (event.key === key) {
        setState(readLocalStorage(key, schema))
      }
    })
    return [
      state,
      useCallback(
        (value) => {
          const newValue = typeof value === 'function' ? value(state) : value
          const newState = schema.safeParse(newValue)
          if (newState.success) {
            updateLocalStorage(key, newState.data)
            setState(newState.data)
            window.dispatchEvent(new StorageEvent('storage', { key }))
          } else {
            logger.error({
              scope: '/app/hooks/browser/createLocalStorageHook.ts',
              detail: 'Failed to update local storage - bad data',
              metadata: { error: newState.error, key, newValue },
            })
          }
        },
        [logger, state]
      ),
    ]
  }
}
